import React from 'react';
import { Button, Checkbox, Popconfirm, message } from 'antd';
import { CheckOutlined, CloseOutlined, ReloadOutlined, UndoOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { anonApi } from '../../util/anonymizerApi';
import RedactionChip from '../../common/RedactionChip';

/** render the masked preview as inline text + redaction chips (only the
 * selected findings applied; the server recomposes authoritatively on apply) */
function renderMasked(original, findings, selected) {
    const spans = selected.map((i) => findings[i]).filter(Boolean).sort((a, b) => a.start - b.start);
    const text = String(original === null || original === undefined ? '' : original);
    const nodes = []; let cur = 0; let k = 0;
    spans.forEach((f) => {
        if (f.start > cur) nodes.push(<span key={k++}>{text.slice(cur, f.start)}</span>);
        nodes.push(<RedactionChip key={k++} type={f.type} />);
        cur = f.end;
    });
    if (cur < text.length) nodes.push(<span key={k++}>{text.slice(cur)}</span>);
    return nodes.length ? nodes : <span>{text}</span>;
}

/** render a pre-masked string (no per-finding data) turning <TYPE> into chips */
function renderNewString(str) {
    const parts = String(str === null || str === undefined ? '' : str).split(/(<[A-Z_]+>)/g);
    return parts.map((p, i) => {
        const m = /^<([A-Z_]+)>$/.exec(p);
        return m ? <RedactionChip key={i} type={m[1]} /> : <span key={i}>{p}</span>;
    });
}

const lineBase = { fontSize: 14, lineHeight: 1.55, padding: '10px 13px', borderRadius: 11 };

/** old -> becomes diff for one field; findings (if present) are individually
 * toggleable - e.g. keep the agent's name but mask the email */
const FieldDiff = ({ name, diff, checked, onToggle, findingSel, onFindingToggle }) => {
    const findings = diff.findings || [];
    const hasFindings = findings.length > 0;
    // indices already applied automatically at run time (policy, not the desk's
    // to reject); the rest are the review types awaiting a human decision
    const auto = Array.isArray(diff.auto) ? diff.auto : [];
    const reviewIdx = findings.map((_, i) => i).filter((i) => auto.indexOf(i) === -1);
    const original = String(diff.old === null || diff.old === undefined ? '' : diff.old);
    return (
        <div style={{ padding: '15px 0', borderTop: '1px solid var(--sc-border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <Checkbox checked={checked} onChange={(e) => onToggle(e.target.checked)} />
                <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--sc-ink-3)' }}>{name}</span>
                <span style={{ marginLeft: 'auto', fontSize: 12.5, color: checked ? 'var(--sc-ink-2)' : 'var(--sc-ink-3)' }}>{checked ? 'included' : 'skipped'}</span>
            </div>
            <div style={{ opacity: checked ? 1 : 0.5, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ ...lineBase, background: 'var(--sc-inset)', color: 'var(--sc-ink-3)', textDecoration: 'line-through', textDecorationColor: 'var(--sc-border-strong)' }}>
                    {original ? original.slice(0, 320) : '(empty)'}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--sc-ink-3)', fontSize: 11, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', margin: '0 0 0 4px' }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M6 13l6 6 6-6" /></svg> becomes
                </div>
                <div style={{ ...lineBase, background: 'var(--sc-ok-soft)', border: '1px solid #285C46', color: 'var(--sc-ink)' }}>
                    {hasFindings ? renderMasked(diff.old, findings, findingSel) : renderNewString(diff.new)}
                </div>
                {hasFindings && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 4 }}>
                        {auto.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                                <span style={{ fontSize: 12, color: 'var(--sc-ink-3)' }}>Already applied:</span>
                                {auto.map((i) => {
                                    const f = findings[i];
                                    if (!f) return null;
                                    return (
                                        <span key={'a' + i} title="applied automatically by policy — already redacted"
                                            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '3px 10px', borderRadius: 99, background: 'var(--sc-inset)', color: 'var(--sc-ink-3)', border: '1px solid var(--sc-border)' }}>
                                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="4" y="11" width="16" height="9" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></svg>
                                            <span style={{ fontFamily: 'var(--sc-mono)', fontWeight: 700, color: 'var(--sc-ink-2)' }}>{f.type}</span>
                                        </span>
                                    );
                                })}
                            </div>
                        )}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                            <span style={{ fontSize: 12, color: 'var(--sc-ink-3)' }}>{auto.length > 0 ? 'Your decision:' : 'Hiding:'}</span>
                            {reviewIdx.length === 0 && <span style={{ fontSize: 12.5, color: 'var(--sc-ink-3)' }}>nothing left to decide</span>}
                            {reviewIdx.map((i) => {
                                const f = findings[i];
                                return (
                                    <Checkbox
                                        key={i}
                                        checked={findingSel.indexOf(i) !== -1}
                                        disabled={!checked}
                                        onChange={(e) => onFindingToggle(i, e.target.checked)}
                                    >
                                        <span style={{ fontFamily: 'var(--sc-mono)', fontSize: 11, fontWeight: 700, color: 'var(--sc-brand-ink)', marginRight: 6 }}>{f.type}</span>
                                        <span style={{ fontSize: 12.5, color: 'var(--sc-ink-2)' }}>{original.slice(f.start, f.end)}</span>
                                    </Checkbox>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const chipNeutral = {
    display: 'inline-flex', alignItems: 'center', fontSize: 12.5, fontWeight: 600,
    padding: '3px 10px', borderRadius: 99, background: 'var(--sc-inset)', color: 'var(--sc-ink-2)',
};

/** The body of one proposal: field diffs + reassurance + apply/dismiss. */
const ReviewBody = ({ entry, onDone }) => {
    const fieldNames = Object.keys(entry.fields || {});
    const [selected, setSelected] = React.useState(fieldNames);
    const [findingSel, setFindingSel] = React.useState(() => {
        const init = {};
        fieldNames.forEach((f) => { init[f] = (entry.fields[f].findings || []).map((_, i) => i); });
        return init;
    });
    const [busy, setBusy] = React.useState(false);

    const apply = () => {
        const findings = {};
        selected.forEach((f) => {
            const all = (entry.fields[f].findings || []).length;
            if (all > 0 && findingSel[f].length < all) findings[f] = findingSel[f];
        });
        setBusy(true);
        anonApi('/v1/apply', {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ id: entry.id, fields: selected, findings })
        })
            .then(() => { message.success(`${entry.recordId}: ${selected.length} field(s) anonymized`); onDone(); })
            .catch((e) => message.error('Apply failed: ' + e.message))
            .finally(() => setBusy(false));
    };

    const dismiss = () => {
        setBusy(true);
        anonApi(`/v1/pending/${entry.id}/dismiss`, { method: 'POST' })
            .then(() => { message.info(`${entry.recordId} dismissed - nothing changed`); onDone(); })
            .catch((e) => message.error('Dismiss failed: ' + e.message))
            .finally(() => setBusy(false));
    };

    return (
        <div style={{ padding: '0 20px' }}>
            {fieldNames.map((f) => (
                <FieldDiff
                    key={f}
                    name={f}
                    diff={entry.fields[f]}
                    checked={selected.indexOf(f) !== -1}
                    onToggle={(on) => setSelected(on ? [...selected, f] : selected.filter((x) => x !== f))}
                    findingSel={findingSel[f] || []}
                    onFindingToggle={(i, on) => setFindingSel({
                        ...findingSel,
                        [f]: on ? [...findingSel[f], i] : findingSel[f].filter((x) => x !== i)
                    })}
                />
            ))}
            <div style={{
                display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
                padding: '15px 0 3px', borderTop: '1px solid var(--sc-border)'
            }}>
                <span style={{ fontSize: 13, color: 'var(--sc-ink-2)', maxWidth: '52ch', lineHeight: 1.45 }}>
                    <b style={{ color: 'var(--sc-ink)' }}>You're in control.</b> Only the ticked items are hidden.
                    The original is kept safely for the retention period — you can undo this anytime.
                </span>
                <span style={{ marginLeft: 'auto' }} />
                <Popconfirm title="Remove this proposal without changing the ticket?" onConfirm={dismiss}>
                    <Button icon={<CloseOutlined />} loading={busy}>Dismiss</Button>
                </Popconfirm>
                <Popconfirm title={`Anonymize ${selected.length} field(s) of ${entry.recordId} in ${entry.form}?`} onConfirm={apply} okText="Apply">
                    <Button type="primary" icon={<CheckOutlined />} loading={busy} disabled={selected.length === 0}>
                        Apply · {selected.length} field{selected.length === 1 ? '' : 's'}
                    </Button>
                </Popconfirm>
            </div>
        </div>
    );
};

/** One proposal as a self-contained, set-apart card that expands on click. */
const ReviewCard = ({ entry, onDone, defaultOpen }) => {
    const [open, setOpen] = React.useState(!!defaultOpen);
    const nFields = Object.keys(entry.fields || {}).length;
    return (
        <div style={{
            background: 'var(--sc-surface)', border: '1px solid var(--sc-border)',
            borderRadius: 16, boxShadow: 'var(--sc-sh-1)', marginBottom: 12, overflow: 'hidden',
            transition: 'box-shadow .15s', ...(open ? { boxShadow: 'var(--sc-sh-2)' } : {})
        }}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                style={{
                    display: 'flex', alignItems: 'center', gap: 12, width: '100%',
                    padding: '15px 20px', background: 'none', border: 'none', cursor: 'pointer',
                    textAlign: 'left', font: 'inherit', color: 'var(--sc-ink)'
                }}
            >
                <span style={{ color: 'var(--sc-ink-3)', fontSize: 18, width: 16, lineHeight: 1, flex: 'none' }}>{open ? '−' : '+'}</span>
                <b style={{ fontSize: 14.5, letterSpacing: '-.01em' }}>{entry.ticket || '—'}</b>
                <span style={{ color: 'var(--sc-ink-3)', fontSize: 13 }}>{entry.form} · {entry.recordId}</span>
                <span style={{ marginLeft: 'auto' }} />
                <span style={{ color: 'var(--sc-ink-3)', fontSize: 12.5 }}>{nFields} field{nFields === 1 ? '' : 's'}</span>
                {entry.profile ? <span style={chipNeutral}>profile: {entry.profile}</span> : null}
                <span style={{ color: 'var(--sc-ink-3)', fontSize: 12.5, fontVariantNumeric: 'tabular-nums' }}>
                    {new Date(entry.createdAt).toLocaleString()}
                </span>
            </button>
            {open && (
                <div style={{ borderTop: '1px solid var(--sc-border)', background: 'var(--sc-surface)', paddingBottom: 16 }}>
                    <ReviewBody entry={entry} onDone={onDone} />
                </div>
            )}
        </div>
    );
};

/**
 * Service-desk review: pending anonymization proposals (from runs whose
 * entries are in "review" mode) as set-apart cards with per-field approval,
 * plus restore search over the audit log (by ticket or record id).
 */
const ApprovalsPanel = () => {
    const [pending, setPending] = React.useState([]);
    const navigate = useNavigate();

    const load = React.useCallback(() => {
        anonApi('/v1/pending', { method: 'GET' })
            .then((r) => setPending(Array.isArray(r) ? r : []))
            .catch(() => setPending([]));
    }, []);

    React.useEffect(() => { load(); }, [load]);

    return (
        <div style={{ marginBottom: 20 }}>
            {/* ---- Pending approvals: set-apart review cards ---- */}
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 680, letterSpacing: '-.01em' }}>Pending approvals</h2>
                <span style={{
                    fontSize: 12.5, fontWeight: 700, color: pending.length ? 'var(--sc-brand-ink)' : 'var(--sc-ok)',
                    background: pending.length ? 'var(--sc-brand-soft)' : 'var(--sc-ok-soft)', padding: '2px 10px', borderRadius: 99
                }}>{pending.length}</span>
                <span style={{ color: 'var(--sc-ink-2)', fontSize: 14 }}>
                    — changes waiting for your review. Nothing is written until you approve.
                </span>
                <span style={{ marginLeft: 'auto' }} />
                <Button icon={<ReloadOutlined />} onClick={load}>Refresh</Button>
            </div>

            {pending.length === 0 ? (
                <div style={{
                    background: 'var(--sc-surface)', border: '1px dashed var(--sc-border-strong)', borderRadius: 16,
                    padding: '40px 20px', textAlign: 'center', color: 'var(--sc-ink-2)'
                }}>
                    You're all caught up — no changes are waiting for approval.
                </div>
            ) : (
                pending.map((entry, i) => (
                    <ReviewCard key={entry.id} entry={entry} onDone={load} defaultOpen={i === 0} />
                ))
            )}

            {/* ---- Undo lives in its own area now ---- */}
            <div style={{
                display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 20,
                padding: '14px 18px', background: 'var(--sc-surface)', border: '1px solid var(--sc-border)', borderRadius: 14
            }}>
                <span style={{ fontSize: 13.5, color: 'var(--sc-ink-2)' }}>
                    Need to undo an anonymization? Every applied change stays restorable for the retention period.
                </span>
                <span style={{ marginLeft: 'auto' }} />
                <Button icon={<UndoOutlined />} onClick={() => navigate('/restore')}>Restore data</Button>
            </div>
        </div>
    );
};

export default ApprovalsPanel;
